import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import BookResourceFlyoverResourceContents from './BookResourceFlyoverResource/BookResourceFlyoverResourceContents';
import BookResourceFlyoverResourceFooter from './BookResourceFlyoverResource/BookResourceFlyoverResourceFooter';
import BookResourceFlyoverBooking from './BookResourceFlyoverBooking/BookResourceFlyoverBooking';
import { ResourceTypeSelector } from 'v1/components/shared';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from 'v5/design-system/Dialog.js';
import { maybe } from '@passionware/monads';
import { ComboboxInput, InlineCombobox } from 'v5/design-system/Combobox.js';
import { CommandInput } from 'cmdk';
import { cn } from 'v5/platform/dom/cn.js';
import { inputVariants } from 'v5/design-system/Input.js';
import useEvent from 'v1/helpers/hooks/useEvent.js';
import { displayAlert, openModal } from 'store/v1/ui/ui.actions.js';
import { BOOKING_ACTIONS } from 'store/v1/bookings/bookings.constants.js';
import BookResourceFlyoverBookingFooter from 'v1/components/shared/byModel/Resource/BookResourceFlyover/BookResourceFlyoverBooking/BookResourceFlyoverBookingFooter.jsx';
import { createSimpleEvent } from '@passionware/simple-event';
import { myServices } from 'v5/services/services.connected.app.js';

const BookResourceFlyover = ({ className, slot, children }) => {
  const dispatch = useDispatch();
  const slotCategories = useSelector(state => state.slot_categories.data);
  const slotCategory = get(slotCategories, [slot.slot_category_id], {});
  const error = useSelector(state => state.bookings.error);

  const [resourceId, setResourceId] = useState();
  const resource = myServices.resourceService.useItem(resourceId);

  const onFlyoverClose = () => setResourceId(null);

  const [resourceTypeId, setResourceTypeId] = useState(null);

  const saveSignal = useMemo(() => createSimpleEvent(), []);

  useEvent([BOOKING_ACTIONS.CREATE_BOOKING], {
    onFailure: () => {
      if (get(error, 'status') === 409) {
        return dispatch(
          openModal('BookingConflictModal', {
            method: 'UPDATE',
            resourceId
          })
        );
      }
    }
  });

  return (
    <Dialog onClose={onFlyoverClose}>
      <DialogTrigger
        asChild
        className={className}
        onClick={() => setResourceId(null)}
      >
        {children}
      </DialogTrigger>
      <DialogContent className="p-0 flex flex-col min-w-150 max-h-1/2">
        <InlineCombobox size="md">
          <DialogHeader className="">
            <DialogTitle>
              {maybe.isPresent(resourceId) ? (
                <>
                  <img src="/images/icon_resources_pear.svg" alt="" />
                  Booking for {resource?.data?.full_name}
                </>
              ) : (
                <>
                  <img src="/images/icon_resources_pear.svg" alt="" />
                  Assign to {slotCategory.name || 'resource'}
                </>
              )}
            </DialogTitle>
            {maybe.isAbsent(resourceId) && (
              <>
                <div className="flex items-center p-4 gap-2">
                  <ComboboxInput asChild>
                    <CommandInput
                      className={cn(
                        'flex-1',
                        inputVariants({ size: 'sm', variant: 'regular' })
                      )}
                      aria-expanded={false}
                    />
                  </ComboboxInput>
                  <div
                    className="contents"
                    onKeyDown={e => {
                      // https://github.com/pacocoursey/cmdk/issues/353
                      e.stopPropagation();
                    }}
                  >
                    <ResourceTypeSelector
                      size="S"
                      label={null}
                      className="w-auto shrink-0"
                      allowNull="All"
                      value={resourceTypeId}
                      onChange={setResourceTypeId}
                    />
                  </div>
                </div>
                <DialogDescription className="p-4 pt-0">
                  Quickly assign and book a resource
                </DialogDescription>
              </>
            )}
          </DialogHeader>
          <DialogBody className="p-0">
            {maybe
              .journey(resourceId)
              .ifPresent(resourceId => (
                <BookResourceFlyoverBooking
                  slot={slot}
                  resourceId={resourceId}
                  saveSignal={saveSignal}
                />
              ))
              .orElse(
                <BookResourceFlyoverResourceContents
                  onResourceAssign={setResourceId}
                  resourceTypeId={resourceTypeId}
                />
              )}
          </DialogBody>
          <DialogFooter className="">
            {maybe
              .journey(resourceId)
              .ifPresent(resourceId => (
                <BookResourceFlyoverBookingFooter
                  saveBooking={saveSignal.emit}
                  onBackClick={() => setResourceId(null)}
                />
              ))
              .orElse(
                <BookResourceFlyoverResourceFooter
                  onResourceAssign={setResourceId}
                  slot={slot}
                  resourceTypeId={resourceTypeId}
                />
              )}
          </DialogFooter>
        </InlineCombobox>
      </DialogContent>

      {/* TODO tooltip? special variant of it? tooltip trigger= popover content? */}
      {/*<Flyover.Helper*/}
      {/*  title={`Assign to ${*/}
      {/*    resource.full_name || slotCategory.name || 'resource'*/}
      {/*  }`}*/}
      {/*>*/}
      {/*  <p>Quickly assign and book a resource</p>*/}
      {/*</Flyover.Helper>*/}
    </Dialog>
  );
};

BookResourceFlyover.propTypes = {
  className: PropTypes.string,
  slot: PropTypes.object,
  children: PropTypes.any
};

export default BookResourceFlyover;
